import { Box, Button, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Download, SystemUpdate } from "@mui/icons-material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";
import LoadingBackDrop from "../../global/LoadingBackDrop";
import { setPutPayloadWithParams } from "../../../services/api";
import PDFViewerDialog from "../../jasperReports/PDFViewerDialog";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Asia/Colombo");

const DailyCashFlow = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs(new Date()));
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCashFlowDialog, setOpenCashFlowDialog] = useState(false);

  const handleCloseCashFlow = () => {
    setOpenCashFlowDialog(false);
  };

  const handleLoadCashFlow = async () => {
    setLoading(true);
    try {
      const response = await setPutPayloadWithParams.setPayloadData(
        "/cashFlow/dailyCashFlow",
        null,
        {
          date: selectedDate.format("YYYY-MM-DD"),
        }
      );
      setTableData(response.data || []);
    } catch (error) {
      console.log(error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "0.00";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  };

  // Format balance with brackets for negative values
  const formatBalance = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "0.00";

    if (numValue < 0) {
      // For negative values: format the absolute value with brackets
      const absoluteValue = Math.abs(numValue);
      return `(${formatNumber(absoluteValue)})`;
    }
    return formatNumber(numValue);
  };

  // Calculate totals
  const calculateTotals = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return { creditTotal: 0, debitTotal: 0, netBalance: 0 };
    }

    const creditTotal = tableData.reduce(
      (sum, row) => sum + (parseFloat(row.credit) || 0),
      0
    );
    const debitTotal = tableData.reduce(
      (sum, row) => sum + (parseFloat(row.debit) || 0),
      0
    );
    const netBalance = creditTotal - debitTotal;

    return { creditTotal, debitTotal, netBalance };
  }, [tableData]);

  // Format net balance with brackets for negative
  const formatNetBalance = useMemo(() => {
    const netBalance = calculateTotals.netBalance;
    if (netBalance < 0) {
      const absoluteValue = Math.abs(netBalance);
      return `(${formatNumber(absoluteValue)})`;
    }
    return formatNumber(netBalance);
  }, [calculateTotals.netBalance]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ cell }) => (
          <Typography
            variant="body1"
            sx={{
              textAlign: "left",
              width: "100%",
              fontWeight: 500,
              pl: 1,
            }}
          >
            {cell.getValue()}
          </Typography>
        ),
        size: 300,
        Footer: () => (
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", textAlign: "left" }}
          >
            Total
          </Typography>
        ),
      },
      {
        accessorKey: "credit",
        header: "Credit",
        size: 150,
        Cell: ({ cell }) => (
          <Typography
            variant="body1"
            sx={{ textAlign: "right", width: "100%", pr: 2 }}
          >
            {formatNumber(cell.getValue())}
          </Typography>
        ),
        Header: () => (
          <Box sx={{ textAlign: "right", width: "100%" }}>Credit</Box>
        ),
        Footer: () => (
          <Box sx={{ textAlign: "right", width: "100%", pr: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {formatNumber(calculateTotals.creditTotal)}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "debit",
        header: "Debit",
        size: 150,
        Cell: ({ cell }) => (
          <Typography
            variant="body1"
            sx={{ textAlign: "right", width: "100%", pr: 2 }}
          >
            {formatNumber(cell.getValue())}
          </Typography>
        ),
        Header: () => (
          <Box sx={{ textAlign: "right", width: "100%" }}>Debit</Box>
        ),
        Footer: () => (
          <Box sx={{ textAlign: "right", width: "100%", pr: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {formatNumber(calculateTotals.debitTotal)}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "balance",
        header: "Balance",
        size: 150,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          const isNegative = !isNaN(numValue) && numValue < 0;

          return (
            <Typography
              variant="body1"
              sx={{
                textAlign: "right",
                width: "100%",
                pr: 2,
                color: isNegative ? "#d32f2f" : "#2e7d32",
                fontWeight: "600",
              }}
            >
              {formatBalance(value)}
            </Typography>
          );
        },
        Header: () => (
          <Box sx={{ textAlign: "right", width: "100%" }}>Balance</Box>
        ),
        Footer: () => (
          <Box sx={{ textAlign: "right", width: "100%", pr: 2 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: calculateTotals.netBalance < 0 ? "#d32f2f" : "#2e7d32",
              }}
            >
              {formatNetBalance}
            </Typography>
          </Box>
        ),
      },
    ],
    [calculateTotals, formatNetBalance]
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,

    // Enable features
    enableColumnFilters: false,
    enablePagination: true,
    enableBottomToolbar: true,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableSorting: false,

    // THIS IS CRITICAL - Enable column footer
    enableColumnFooter: true,

    initialState: {
      density: "compact",
      pagination: { pageSize: 50, pageIndex: 0 },
    },

    // Pagination options
    paginationDisplayMode: "pages",

    muiPaginationProps: {
      rowsPerPageOptions: [50, 100, 200],
      showFirstButton: true,
      showLastButton: true,
      variant: "outlined",
      shape: "rounded",
    },

    // Table container styles
    muiTableContainerProps: {
      sx: {
        maxHeight: "calc(100vh - 250px)",
        border: "1px solid #e0e0e0",
        borderRadius: 1,
      },
    },

    // Table props
    muiTableProps: {
      sx: {
        borderCollapse: "separate",
      },
    },

    // Table head props
    muiTableHeadProps: {
      sx: {
        "& th": {
          backgroundColor: "#f5f5f5",
          fontWeight: "bold",
          borderBottom: "2px solid #ddd",
        },
      },
    },

    // Table body row props
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        "&:hover": {
          backgroundColor: "#f9f9f9",
        },
        "&:nth-of-type(odd)": {
          backgroundColor: "#fafafa",
        },
      },
    }),

    // Table body cell props
    muiTableBodyCellProps: {
      sx: {
        borderRight: "1px solid #f0f0f0",
      },
    },

    // Footer row props - IMPORTANT for styling footer
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: "#e8f5e9",
        "& td": {
          borderTop: "2px solid #4caf50",
          fontWeight: "bold",
          fontSize: "0.95rem",
          py: 1.5,
        },
      },
    },

    // Footer cell props
    muiTableFooterCellProps: ({ column }) => ({
      sx: {
        textAlign: column.id === "description" ? "left" : "right",
        pr: column.id === "description" ? 1 : 2,
      },
    }),

    // Optional: Add bottom toolbar props
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        borderTop: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <Box sx={{ p: 3 }}>
      {loading && <LoadingBackDrop openBackDrop={loading} />}

      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Box display="flex" gap={1} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ width: 200 }}>
              <DatePicker
                label="Select Date"
                format="YYYY-MM-DD"
                value={selectedDate}
                onChange={(newValue) => setSelectedDate(newValue)}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Box>
          </LocalizationProvider>

          <Button
            variant="contained"
            startIcon={<SystemUpdate />}
            onClick={handleLoadCashFlow}
            disabled={loading}
            sx={{
              height: 40,
              px: 3,
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            LOAD DATA
          </Button>

          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={() => setOpenCashFlowDialog(true)}
            disabled={loading}
            sx={{
              height: 40,
              px: 3,
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            DOWNLOAD REPORT
          </Button>

          {tableData.length > 0 && (
            <Typography sx={{ ml: "auto", color: "text.secondary" }}>
              {tableData.length} records loaded
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 1,
          overflow: "hidden",
        }}
      >
        <MaterialReactTable table={table} />
      </Box>
      <PDFViewerDialog
        reportNo={`CASH_FLOW_${selectedDate}`}
        labRequestId={selectedDate.format("YYYY-MM-DD")}
        open={openCashFlowDialog}
        handleCloseReportPreview={handleCloseCashFlow}
        letterHead={""}
        reportType={"CASHFLOW"}
      />
    </Box>
  );
};

export default DailyCashFlow;
